import { Injectable } from '@angular/core';
import { Notification } from '@models/notification';
import { MenuService } from '@services/menu/menu.service';
import { IndividualConfig, Toast, ToastrService } from 'ngx-toastr';
import { ToastLevel } from '@shared/toasterSetup';
import { BehaviorSubject } from 'rxjs';
import { take } from "rxjs/operators";
import { tap } from 'rxjs/operators';
import { ToastPackage } from 'ngx-toastr';
import { CustomToastComponent} from '@shared/custom-toast/custom-toast-component';
import { CommonModule } from '@angular/common';

// Toaster encapsulated helpers
const onDiscard = (component, page: string) => {
  // console.log('discard');
  component.menu.dialogSaveButton = {
    valid: true,
    clicked: false,
    enabled: false,
    page,
    source: `${page}-discard`,
  };
};

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  public numberOfActiveToasts = 0;
  public mainNotificationBanner$ = new BehaviorSubject(null);

  private currentId;
  private _notificationList$: BehaviorSubject<Map<number, Notification>>;

  public get notificationList(): Map<number, Notification> {
    return this.notificationList$.getValue();
  }
  public set notificationList(value: Map<number, Notification>) {
    this.notificationList$.next(value);
  }
  public get notificationList$(): BehaviorSubject<Map<number, Notification>> {
    return this._notificationList$;
  }
  public set notificationList$(value: BehaviorSubject<Map<number, Notification>>) {
    this._notificationList$ = value;
  }

  public getNotificationListByType(type: string): Map<number, Notification> {
    const map = [...this.notificationList].filter(([k, v]) => v.type === type);
    return new Map<number, Notification>(map);
  }

  constructor(
    private toasterService: ToastrService, 
    private menu: MenuService
  ) {
    this.notificationList$ = new BehaviorSubject<Map<number, Notification>>(new Map<number, Notification>());
    this.currentId = 0;
  }

  public addNewNotification(type: string, title: string, description: string, references?: any) {
    const timestamp = Date.now();
    this.notificationList.set(this.currentId++, { type, title, description, references, timestamp });
    this.notificationList$.next(this.notificationList);
    return this.currentId - 1;
  }

  public deleteNotificationById(id: number) {
    this.notificationList.delete(id);
    this.notificationList$.next(this.notificationList);
  }

  public deleteAllNotificationsByType(type: string) {
    const newNotificationList = [...this.notificationList].filter(([k, v]) => v.type !== type);
    this.notificationList$.next(new Map<number, Notification>(newNotificationList));
  }

  public updateNotificationById(id: number, newNotification: object) {
    const oldNotification = this.notificationList.get(id);
    this.notificationList.set(id, { ...oldNotification, ...newNotification });
    this.notificationList$.next(this.notificationList);
  }

  /**
   * Delete all the notifications stored in the systems for a particular mnemonic and type
   * @param mnemonic Memonic will be used as filter for the deletion
   * @param type Optional parameter if you want to filter for a specific type of notification
   */
  public deleteNotificationsByMnemonic(mnemonic: string, type?: string) {
    const newMap = [...this.notificationList].filter(([k, v]) => {
      if (type) {
        return !(v.type === type && v.references.mnemonic === mnemonic);
      } else {
        return v.references.mnemonic !== mnemonic;
      }
    });
    this.notificationList = new Map<number, Notification>(newMap);
    this.notificationList$.next(this.notificationList);
  }

  // -------------------------------------
  //              TOASTS
  // -------------------------------------

  /**
   *
   * @param toastToDisplay Toast object
   * @param buttonText Button Text
   * @param routerLink Router Link to navigate when clicking the button
   */
  public showToast(
    level: ToastLevel,
    toast: Toast,
    buttonText: string = null,
    routerLink: string = null,
    bodyIsActionButton = false,
    injectedComponent = null,
    useHtml: boolean = true,
    showCloseButton: boolean = true
  ) {
    let options = {
      closeButton: showCloseButton,
      newestOnTop: false,
      progressBar: false,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      onclick: null,
      enableHtml: useHtml,
    } as unknown as IndividualConfig;

    let toastToDisplay: any = { ...toast };
    const hasButton = buttonText !== null && routerLink !== null;

    if (hasButton) {
      // Create button configuration for CustomToastComponent
      const buttonConfig = {
        name: buttonText,
        class: 'btn-toast-action',
        action: () => {
          // Navigate to the specified route
          if (routerLink) {
            this.menu.navigateTo(routerLink);
          }
        }
      };

      options = {
        ...options,
        buttons: [buttonConfig],
        toastComponent: CustomToastComponent
      } as any;
    }
    
    this.numberOfActiveToasts++;
    switch (level) {
      case ToastLevel.Warning: {
        return this.toasterService.warning(toast?.message, toast?.title, options);
      }
      case ToastLevel.Success: {
        return this.toasterService.success(toast?.message, toast?.title, options);
      }
      case ToastLevel.Error: {
        return this.toasterService.error(toast?.message, toast?.title, options);
      }
      case ToastLevel.Information:
      default: {
        return this.toasterService.info(toast?.message, toast?.title, options);
      }
    }
  }

  public clear() {
    this.toasterService.clear();
    this.numberOfActiveToasts = 0;
  }

  public hideToasts() {
    this.toasterService.clear();
    this.numberOfActiveToasts = 0;
  }

  public hideToast(id) {
    this.toasterService.clear(id);
    this.numberOfActiveToasts--;
  }

  public showToastWithButtons(level: ToastLevel, title: string, body: string, buttons: any[]) {
    let options = {
      closeButton: true,
      newestOnTop: false,
      progressBar: false,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      onclick: null,
      enableHtml: true,
      buttons: buttons,
      toastComponent: CustomToastComponent
    } as any;

    this.numberOfActiveToasts++;
    switch (level) {
      case ToastLevel.Warning: {
        return this.toasterService.warning(body, title, options);
      }
      case ToastLevel.Success: {
        return this.toasterService.success(body, title, options);
      }
      case ToastLevel.Error: {
        return this.toasterService.error(body, title, options);
      }
      case ToastLevel.Information:
      default: {
        return this.toasterService.info(body, title, options);
      }
    }
  }

  toasterClickedHandler(buttons: any[]) {
    console.log('Toastr clicked');

    buttons.forEach(button => {
      const thisButtonClicked = (<any>event).srcElement.className.includes(button.class);
      if (thisButtonClicked) {
        if (button.funct) {
          button.funct();
        }
        return true;
      }
    });
  }
}
